import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Decision,
  Project,
  Entity,
  AwaitingEntity,
  Action,
  ActionRespondAction,
  Discrepancy,
  Thread365Message,
  CostByTaskResponse,
  WorkByProductResponse,
} from "../types";
import { getIdToken, signIn } from "../auth/cognito";

type RatifyPayload = {
  action: "decide" | "discuss";
  chosen_key?: string;
  chosen_other_text?: string;
  reasoning?: string;
  discuss_reason?: string;
};

type ActionRespondPayload = {
  action: ActionRespondAction;
  reason_for_decline?: string;
};

type State = {
  decisions: Decision[];
  projects: Project[];
  entities: Entity[];
  awaiting: AwaitingEntity[];
  recentlyDecided: Decision[];
  needsDiscussion: Decision[];
  actions: Action[];
  recentlyDoneActions: Action[];
  parkedActions: Action[];
  discrepancies: Discrepancy[];
  recentlyDeclinedActions: Action[];
  /** T3-P1b: 365 response channel messages (sorted oldest-first). */
  thread365: Thread365Message[];
  /** BOARDROOM-2026-08-08-213816: token cost/usage grouped by task_ref +
   *  entity_id, sourced from trumin-session-telemetry. Null until the first
   *  successful fetch (endpoint may 404 pre-deploy / telemetry_available may
   *  be false while the IAM grant is pending — both best-effort, non-fatal). */
  costByTask: CostByTaskResponse | null;
  /** BOARDROOM-2026-08-08-135707: per-product derived-work view. Empty
   *  products map until the first successful fetch. */
  workByProduct: WorkByProductResponse | null;
  loading: boolean;
  error: string | null;
  ratify: (ref: string, payload: RatifyPayload) => Promise<void>;
  respondAction: (ref: string, payload: ActionRespondPayload) => Promise<void>;
  /** BOARDROOM-2026-08-08-124017 step 2: one-click dismiss for ANY card
   *  (decision-card or action-card). Calls
   *  POST /api/cards/{ref}/dismiss. Refetches on success. */
  dismissCard: (ref: string) => Promise<void>;
  /** Fetch a single decision row with body included.
   *  Calls GET /api/decisions/{id}?include_body=true (sub-agent C contract).
   *  Returns null if endpoint not yet available (404/501/network) — callers
   *  should fall back to "Body not yet available — pending API change". */
  fetchDecisionBody: (id: string) => Promise<Decision | null>;
  /** T3-P1b: Send a free-text instruction to boardroom-365.
   *  Calls POST /api/365/instruct with {text}.
   *  Refreshes thread365 after a successful send.
   *  Throws on non-OK HTTP response. */
  sendInstruction: (text: string) => Promise<{ section_id: string }>;
};

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";
const POLL_MS = 15_000;

export function useDashboard(): State {
  // Initialise empty -- never silently render mock data. Mock fixtures live in
  // src/data/mock.ts but are imported only by tests; tree-shaking keeps them
  // out of the production bundle.
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [awaiting, setAwaiting] = useState<AwaitingEntity[]>([]);
  const [recentlyDecided, setRecentlyDecided] = useState<Decision[]>([]);
  const [needsDiscussion, setNeedsDiscussion] = useState<Decision[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [recentlyDoneActions, setRecentlyDoneActions] = useState<Action[]>([]);
  const [parkedActions, setParkedActions] = useState<Action[]>([]);
  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  const [recentlyDeclinedActions, setRecentlyDeclinedActions] = useState<Action[]>([]);
  // T3-P1b: 365 response channel
  const [thread365, setThread365] = useState<Thread365Message[]>([]);
  // BOARDROOM-2026-08-08-213816: token cost-per-task/entity
  const [costByTask, setCostByTask] = useState<CostByTaskResponse | null>(null);
  // BOARDROOM-2026-08-08-135707: per-product derived-work view
  const [workByProduct, setWorkByProduct] = useState<WorkByProductResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const authedFetch = useCallback(
    async (url: string, init?: RequestInit): Promise<Response> => {
      const token = await getIdToken();
      if (!token) {
        await signIn();
        // signIn() navigates away; the next line never executes in normal flow.
        throw new Error("Redirecting to sign in");
      }
      const headers = new Headers(init?.headers);
      headers.set("Authorization", `Bearer ${token}`);
      const r = await fetch(url, { ...init, headers });
      if (r.status === 401 || r.status === 403) {
        // Surface the auth failure rather than calling signIn() in a loop.
        // The previous behaviour kicked off a fresh Cognito redirect for
        // every 403; the second redirect's token-exchange would race the
        // first (consumed code -> 400 invalid_grant) and Duncan got stuck
        // in a loop. Likely cause of the 403 itself is APIGW JWT authoriser
        // config (audience / issuer / token_use mismatch) -- infra-side fix.
        throw new Error(
          `API rejected the auth token (${r.status}). Most likely an APIGW JWT ` +
            `authoriser config issue on the backend. Click Debug to inspect the ` +
            `decoded JWT, then ask boardroom to compare its iss/aud claims to the ` +
            `aws_apigatewayv2_authorizer config in the infra terraform.`,
        );
      }
      return r;
    },
    [],
  );

  const load = useCallback(async () => {
    if (!API_BASE) {
      setError("VITE_API_BASE not configured at build time.");
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const [escR, projR, entR] = await Promise.all([
        authedFetch(`${API_BASE}/api/escalations`, { signal: ctrl.signal }),
        authedFetch(`${API_BASE}/api/projects`, { signal: ctrl.signal }),
        authedFetch(`${API_BASE}/api/entities`, { signal: ctrl.signal }),
      ]);
      if (!escR.ok || !projR.ok || !entR.ok) {
        throw new Error(
          `API error (esc=${escR.status}, proj=${projR.status}, ent=${entR.status})`,
        );
      }
      const [escJ, projJ, entJ] = await Promise.all([
        escR.json(),
        projR.json(),
        entR.json(),
      ]);
      setDecisions(escJ);
      setProjects(projJ);
      setEntities(entJ);
      // /api/awaiting fetch is best-effort: the endpoint may not exist on
      // APIGW yet (added by infra-laptop after the backend handler ships).
      // A 404 here must NOT break the rest of the dashboard. Fetched
      // separately + caught locally so any failure resets to empty list.
      try {
        const awaR = await authedFetch(`${API_BASE}/api/awaiting`, { signal: ctrl.signal });
        if (awaR.ok) {
          setAwaiting(await awaR.json());
        } else {
          setAwaiting([]);
        }
      } catch {
        setAwaiting([]);
      }
      // /api/recently-decided fetch is best-effort for the same reason — the
      // APIGW route may not exist yet. PR3b area E.
      try {
        const rdR = await authedFetch(`${API_BASE}/api/recently-decided`, { signal: ctrl.signal });
        if (rdR.ok) {
          setRecentlyDecided(await rdR.json());
        } else {
          setRecentlyDecided([]);
        }
      } catch {
        setRecentlyDecided([]);
      }
      // /api/needs-discussion fetch — same best-effort pattern. Surfaces
      // discussing-state ESCs in the dashboard's "Needs further discussion"
      // sub-section per BOARDROOM-2026-04-26-162700 v3-revert.
      try {
        const ndR = await authedFetch(`${API_BASE}/api/needs-discussion`, { signal: ctrl.signal });
        if (ndR.ok) {
          setNeedsDiscussion(await ndR.json());
        } else {
          setNeedsDiscussion([]);
        }
      } catch {
        setNeedsDiscussion([]);
      }
      // /api/actions fetch — best-effort; backend route lands with WU-2a impl.
      // 404 must not break the rest of the dashboard during partial deploys.
      try {
        const acR = await authedFetch(`${API_BASE}/api/actions`, { signal: ctrl.signal });
        if (acR.ok) {
          setActions(await acR.json());
        } else {
          setActions([]);
        }
      } catch {
        setActions([]);
      }
      try {
        const adR = await authedFetch(
          `${API_BASE}/api/actions/recently-done`,
          { signal: ctrl.signal },
        );
        if (adR.ok) {
          setRecentlyDoneActions(await adR.json());
        } else {
          setRecentlyDoneActions([]);
        }
      } catch {
        setRecentlyDoneActions([]);
      }
      try {
        const apR = await authedFetch(`${API_BASE}/api/actions/parked`, {
          signal: ctrl.signal,
        });
        if (apR.ok) {
          setParkedActions(await apR.json());
        } else {
          setParkedActions([]);
        }
      } catch {
        setParkedActions([]);
      }
      // /api/discrepancies fetch — best-effort; backend route lands with
      // WU-09 impl.  A 404 must not break the rest of the dashboard during
      // partial deploys.
      try {
        const discR = await authedFetch(`${API_BASE}/api/discrepancies`, {
          signal: ctrl.signal,
        });
        if (discR.ok) {
          setDiscrepancies(await discR.json());
        } else {
          setDiscrepancies([]);
        }
      } catch {
        setDiscrepancies([]);
      }
      // /api/actions/recently-declined — BDD D3: declined state surfaces in
      // recently-declined sub-section (24h window) with reason visible.
      try {
        const axR = await authedFetch(
          `${API_BASE}/api/actions/recently-declined`,
          { signal: ctrl.signal },
        );
        if (axR.ok) {
          setRecentlyDeclinedActions(await axR.json());
        } else {
          setRecentlyDeclinedActions([]);
        }
      } catch {
        setRecentlyDeclinedActions([]);
      }
      // T3-P1b: /api/365/thread — best-effort; backend route lands with T3-P1a.
      // A 404 must not break the rest of the dashboard during partial deploys.
      try {
        const t365R = await authedFetch(`${API_BASE}/api/365/thread`, {
          signal: ctrl.signal,
        });
        if (t365R.ok) {
          setThread365(await t365R.json());
        } else {
          setThread365([]);
        }
      } catch {
        setThread365([]);
      }
      // BOARDROOM-2026-08-08-213816: /api/cost-by-task -- best-effort, same
      // pattern as every other endpoint here. A 404 (route not yet deployed)
      // or network failure must not break the rest of the dashboard; a 200
      // with telemetry_available:false (IAM grant pending) is still stored
      // so the panel can render its own "not available yet" state.
      try {
        const cbtR = await authedFetch(`${API_BASE}/api/cost-by-task`, {
          signal: ctrl.signal,
        });
        if (cbtR.ok) {
          setCostByTask(await cbtR.json());
        } else {
          setCostByTask(null);
        }
      } catch {
        setCostByTask(null);
      }
      // BOARDROOM-2026-08-08-135707: /api/work-by-product — best-effort;
      // same partial-deploy tolerance as the other newer endpoints above.
      try {
        const wbpR = await authedFetch(`${API_BASE}/api/work-by-product`, {
          signal: ctrl.signal,
        });
        if (wbpR.ok) {
          setWorkByProduct(await wbpR.json());
        } else {
          setWorkByProduct(null);
        }
      } catch {
        setWorkByProduct(null);
      }

      setError(null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  const ratify = useCallback(
    async (ref: string, payload: RatifyPayload): Promise<void> => {
      if (!API_BASE) {
        throw new Error("VITE_API_BASE not configured at build time.");
      }
      const url = `${API_BASE}/api/decisions/${encodeURIComponent(ref)}/answer`;
      const body = {
        action: payload.action,
        chosen_key: payload.chosen_key,
        chosen_other_text: payload.chosen_other_text,
        reasoning: payload.reasoning,
        discuss_reason: payload.discuss_reason,
        decided_by: "Duncan",
        decided_at: new Date().toISOString(),
      };
      const r = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let detail = "";
        try {
          const j = await r.json();
          detail = j.error || j.detail || JSON.stringify(j);
        } catch {
          detail = r.statusText;
        }
        throw new Error(`POST /api/decisions/${ref}/answer ${r.status}: ${detail}`);
      }
      // Success — refetch immediately so the resolved ESC is filtered out by
      // the backend's decision_state instead of waiting on the 15s poll.
      await load();
    },
    [authedFetch, load],
  );

  /** Fetch a single decision row with body_xml + body_json included.
   *  Contract: GET /api/decisions/{id}?include_body=true returns a Decision
   *  object augmented with body_xml (string | null) and body_json (object | null).
   *
   *  Defensive: returns null on 404/501 (API not yet deployed by sub-agent C)
   *  or any network failure — callers display a pending-API-change notice. */
  const fetchDecisionBody = useCallback(
    async (id: string): Promise<Decision | null> => {
      if (!API_BASE) return null;
      try {
        const url = `${API_BASE}/api/decisions/${encodeURIComponent(id)}?include_body=true`;
        const r = await authedFetch(url);
        if (!r.ok) {
          // 404 = endpoint not yet deployed; 501 = not implemented; treat as pending.
          return null;
        }
        return (await r.json()) as Decision;
      } catch {
        return null;
      }
    },
    [authedFetch],
  );

  const respondAction = useCallback(
    async (ref: string, payload: ActionRespondPayload): Promise<void> => {
      if (!API_BASE) {
        throw new Error("VITE_API_BASE not configured at build time.");
      }
      const url = `${API_BASE}/api/actions`;
      const body: Record<string, unknown> = {
        ref,
        action: payload.action,
        decided_by: "Duncan",
        decided_at: new Date().toISOString(),
      };
      if (payload.action === "decline" && payload.reason_for_decline) {
        body.reason_for_decline = payload.reason_for_decline;
      }
      const r = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let detail = "";
        try {
          const j = await r.json();
          detail = j.error || j.detail || JSON.stringify(j);
        } catch {
          detail = r.statusText;
        }
        throw new Error(
          `POST /api/actions ${r.status}: ${detail}`,
        );
      }
      await load();
    },
    [authedFetch, load],
  );

  // BOARDROOM-2026-08-08-124017 step 2: dismissCard — POST /api/cards/{ref}/dismiss
  const dismissCard = useCallback(
    async (ref: string): Promise<void> => {
      if (!API_BASE) {
        throw new Error("VITE_API_BASE not configured at build time.");
      }
      const url = `${API_BASE}/api/cards/${encodeURIComponent(ref)}/dismiss`;
      const r = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dismissed_by: "Duncan" }),
      });
      if (!r.ok) {
        let detail = "";
        try {
          const j = await r.json();
          detail = j.error || j.detail || JSON.stringify(j);
        } catch {
          detail = r.statusText;
        }
        throw new Error(`POST /api/cards/${ref}/dismiss ${r.status}: ${detail}`);
      }
      await load();
    },
    [authedFetch, load],
  );

  // T3-P1b: sendInstruction — POST /api/365/instruct
  const sendInstruction = useCallback(
    async (text: string): Promise<{ section_id: string }> => {
      if (!API_BASE) {
        throw new Error("VITE_API_BASE not configured at build time.");
      }
      const r = await authedFetch(`${API_BASE}/api/365/instruct`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) {
        let detail = "";
        try {
          const j = await r.json();
          detail = j.error || j.detail || JSON.stringify(j);
        } catch {
          detail = r.statusText;
        }
        throw new Error(`POST /api/365/instruct ${r.status}: ${detail}`);
      }
      const result = await r.json();
      // Refresh thread immediately so Duncan sees the instruction reflected.
      await load();
      return { section_id: result.section_id as string };
    },
    [authedFetch, load],
  );

  useEffect(() => {
    load();
    const interval = window.setInterval(load, POLL_MS);
    return () => {
      window.clearInterval(interval);
      abortRef.current?.abort();
    };
  }, [load]);

  return {
    decisions,
    projects,
    entities,
    awaiting,
    recentlyDecided,
    needsDiscussion,
    actions,
    recentlyDoneActions,
    parkedActions,
    discrepancies,
    recentlyDeclinedActions,
    thread365,
    costByTask,
    workByProduct,
    loading,
    error,
    ratify,
    respondAction,
    dismissCard,
    fetchDecisionBody,
    sendInstruction,
  };
}
